import { CommonModule } from '@angular/common';
import { Component, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RazorPaymentPayload } from '@edtech-ui/core/models/mentors-sessions/create-order-razorpay';
import { RazorPaymentVerifyPayload } from '@edtech-ui/core/models/mentors-sessions/post-razor-payment-verify';
import { CurrentSubscription, PackageProductsResponse, StudentDataPackageProduct } from '@edtech-ui/core/models/student-courses-v2/student-products-lists';
import { PaymentService } from '@edtech-ui/core/services/payment-management/payment.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { PaymentSuccessDialogComponent } from '../payment-success-dialog/payment-success-dialog.component';
import { StudentPaymentActivePayload } from '@edtech-ui/core/models/student-courses-v2/student-payment-activate';
import { StudentsSubscriptionPlan } from '@edtech-ui/constants/current-subscription.enum';
import { ReferralCodeDialogBoxComponent } from '../referral-code-dialog-box/referral-code-dialog-box.component';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
declare var Razorpay: any;

@Component({
	selector: 'app-payment-dialog-box',
	standalone: true,
	imports: [CommonModule,HasPermissionDirective],
	templateUrl: './payment-dialog-box.component.html',
	styleUrl: './payment-dialog-box.component.scss'
})
export class PaymentDialogBoxComponent {
	subscription = new Subscription();
	studentPackage!: StudentDataPackageProduct;
	currentSubscription!: CurrentSubscription;
	freeTrialPackage!: StudentDataPackageProduct;
	productId!: string;
	priceId!: string;
	private orderId!: string;
	private razorpayOrderId!: string;
	private verifyOrderId!: string;
	private verifyPaymentId!: string;
	studentEmail: string | null = null;
	isLoading = false;
	StudentsSubscriptionPlan = StudentsSubscriptionPlan;
	finalAmountAfterDiscount = 0;
	isReferralApplied = false;
	appliedReferralCode: string | null = null;
	useUsageCount = false;
	amountFromCreatedApi = 0;
	PermissionForStudents = PermissionForStudents;

	constructor(
		private dialogRef: MatDialogRef<PaymentDialogBoxComponent>,
		private _toastr: ToastrService,
		private _paymentService: PaymentService,
		private ngZone: NgZone,
		private dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.getStudentProductData();
		this.studentEmail = localStorage.getItem('email');
	}

	getStudentProductData() {
		this.subscription.add(
			this._paymentService.getStudentProducts().pipe(
				tap((response: PackageProductsResponse) => {
					if (response.Success) {
						this.currentSubscription = response.Data.current_subscription;

						const startPricePlan = response.Data.products.find(
							(packageName) => packageName.product_name === StudentsSubscriptionPlan.STARTER_PRO);

						if (startPricePlan) {
							this.studentPackage = startPricePlan;
							this.priceId = startPricePlan.pricing_id;
							this.productId = startPricePlan.product_id;
						}
						this.freeTrialPackage = response.Data.products.find(packagePrice => packagePrice.price === StudentsSubscriptionPlan.PRICE_VALUE)!;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong .Please try again');
					return of(null);
				})
			).subscribe()
		);
	}

	postRazorPayment() {
		const paylod: RazorPaymentPayload = {
			product_id: this.productId,
			pricing_id: this.priceId,
			quantity: 1,
			referral_code: this.appliedReferralCode ?? undefined,
			use_usage_count: this.useUsageCount
		}
		this.isLoading = true;
		this.subscription.add(
			this._paymentService.postCreateOrderRazorPayment(paylod).pipe(
				tap(response => {
					if (response.Success) {
						this.orderId = response.Data.order_id;
						this.razorpayOrderId = response.Data.razorpay_order_id;
						this.amountFromCreatedApi = response.Data.amount;
						this.openRazorPayDialogBox();
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		)
	}

	openRazorPayDialogBox() {

		const amountToPay = this.finalAmountAfterDiscount > 0
			? this.finalAmountAfterDiscount :
			this.studentPackage.price;

		const options = {
			key: environment.razorPayKey,
			amount: amountToPay * 100,
			currency: 'INR',
			description: 'Mentor Session Payment',
			order_id: this.razorpayOrderId,
			prefill: {
				email: this.studentEmail,
			},
			theme: { color: '#3D348B' },
			handler: (response: any) => {
				this.ngZone.run(() => {
					this.postPaymentVerify(response);
				});
			}
		}

		const razorpay = new Razorpay(options);

		razorpay.on('payment.failed', (error: any) => {
			this._toastr.error('Payment failed. Please try again.');
		});
		razorpay.open();
	}

	postPaymentVerify(response: any) {
		const payload: RazorPaymentVerifyPayload = {
			razorpay_order_id: this.razorpayOrderId,
			razorpay_payment_id: response.razorpay_payment_id,
			razorpay_signature: response.razorpay_signature,
			order_id: this.orderId
		}
		this.subscription.add(
			this._paymentService.postRazorPaymentVerify(payload).pipe(
				tap(response => {
					if (response.Success) {
						this.verifyOrderId = response.Data.order_id;
						this.verifyPaymentId = response.Data.payment_id;
						this._toastr.success(response.Message);
						this.postStudentActiveSubscription();
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		)
	}

	postStudentActiveSubscription() {
		const payload: StudentPaymentActivePayload = {
			order_id: this.verifyOrderId,
			payment_id: this.verifyPaymentId
		}
		this.subscription.add(
			this._paymentService.postPaymentActiveSubscription(payload).pipe(
				tap(response => {
					if (response.Success) {
						this.openSuccessDialogBox();
						this.dialogRef.close(true);
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		);
	}

	postStudentFreeActiveSubscription() {
		const payload: StudentPaymentActivePayload = {
			product_id: this.freeTrialPackage.product_id
		}
		this.subscription.add(
			this._paymentService.postPaymentActiveSubscription(payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success('Free Trial activated. Explore with limited access');
						this.dialogRef.close(true);
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		);
	}

	openSuccessDialogBox() {
		const successRef = this.dialog.open(PaymentSuccessDialogComponent, {
			data: { amount: this.amountFromCreatedApi },
			disableClose: true
		});
		successRef.afterClosed().subscribe((success) => {
			if (success) {
				this.dialogRef.close(true);
			}
		})
	}

	openReferralCodeDialogBox() {
		const dialogRef = this.dialog.open(ReferralCodeDialogBoxComponent, {
			data: {
				pricing_id: this.priceId,
				plan_name: this.studentPackage.product_name,
				price: this.studentPackage.price
			},
			disableClose: true
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) return;

			this.isReferralApplied = result.isReferralApplied;

			if (result.isReferralApplied) {
				this.finalAmountAfterDiscount = result.discountedPrice;
				this.appliedReferralCode = result.referralCode;
				this.useUsageCount = result.use_usage_count;
			} else {
				this.finalAmountAfterDiscount = 0
				this.appliedReferralCode = null;
				this.useUsageCount = false;
			}
			this.postRazorPayment();
		})
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
